/**
 * Audio Preloader Service
 *
 * Pre-buffers audio for visible SoundCards to enable near-instant playback.
 * Uses LRU eviction to manage memory (max 6 preloaded sounds).
 */

const isDev = process.env.NODE_ENV === 'development';

interface PreloadedAudio {
  element: HTMLAudioElement;
  url: string;
  ready: boolean;
  timestamp: number;
}

class AudioPreloader {
  private static instance: AudioPreloader;
  private preloadedAudio: Map<string, PreloadedAudio> = new Map();
  private loadingPromises: Map<string, Promise<void>> = new Map();
  private maxPreloaded = 6;

  private constructor() {}

  static getInstance(): AudioPreloader {
    if (!AudioPreloader.instance) {
      AudioPreloader.instance = new AudioPreloader();
    }
    return AudioPreloader.instance;
  }

  /**
   * Preload audio data for a sound. Returns immediately if already preloaded/loading.
   */
  async preload(soundId: string, url: string): Promise<void> {
    // Skip if already preloaded with same URL
    const existing = this.preloadedAudio.get(soundId);
    if (existing && existing.url === url && existing.ready) {
      return;
    }

    // Skip if already loading
    if (this.loadingPromises.has(soundId)) {
      return this.loadingPromises.get(soundId);
    }

    const loadPromise = this.doPreload(soundId, url);
    this.loadingPromises.set(soundId, loadPromise);

    try {
      await loadPromise;
    } finally {
      this.loadingPromises.delete(soundId);
    }
  }

  private async doPreload(soundId: string, url: string): Promise<void> {
    // Evict oldest if at capacity
    this.evictIfNeeded();

    if (isDev) console.log(`[AudioPreloader] Preloading: ${soundId}`);

    const audio = new Audio();
    // Matches hooks/useAudioPlayer.ts: Supabase Storage serves
    // `access-control-allow-origin: *` and /api/audio-proxy mirrors the
    // caller's origin, so 'anonymous' mode loads cleanly for every URL this
    // preloader is given — and without it, a preloaded element handed to
    // useAudioLevel's analyser would read as permanently silent (tainted).
    audio.crossOrigin = 'anonymous';
    audio.preload = 'auto';

    // Store immediately (not ready yet)
    this.preloadedAudio.set(soundId, {
      element: audio,
      url,
      ready: false,
      timestamp: Date.now(),
    });

    // Set source and wait for enough data
    return new Promise<void>((resolve) => {
      const cleanup = () => {
        audio.removeEventListener('canplaythrough', onReady);
        audio.removeEventListener('error', onError);
        clearTimeout(timeoutId);
      };

      const onReady = () => {
        cleanup();
        const entry = this.preloadedAudio.get(soundId);
        if (entry) {
          entry.ready = true;
          if (isDev) console.log(`[AudioPreloader] Ready: ${soundId}`);
        }
        resolve();
      };

      const onError = () => {
        cleanup();
        // Remove failed entry
        this.preloadedAudio.delete(soundId);
        if (isDev) console.log(`[AudioPreloader] Failed: ${soundId}`);
        resolve(); // Don't reject - just skip this preload
      };

      // Timeout after 8 seconds (don't block forever)
      const timeoutId = setTimeout(() => {
        cleanup();
        // Keep the entry even if not fully ready - partial buffer is still useful
        const entry = this.preloadedAudio.get(soundId);
        if (entry && audio.readyState >= 2) {
          entry.ready = true;
          if (isDev) console.log(`[AudioPreloader] Partial ready: ${soundId}`);
        }
        resolve();
      }, 8000);

      audio.addEventListener('canplaythrough', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.src = url;
      audio.load();
    });
  }

  /**
   * Get a preloaded audio element. Transfers ownership - caller must manage the element.
   * Returns null if not preloaded or not ready.
   */
  getPreloaded(soundId: string): HTMLAudioElement | null {
    const entry = this.preloadedAudio.get(soundId);
    if (!entry || !entry.ready) {
      return null;
    }

    // Transfer ownership - remove from cache
    this.preloadedAudio.delete(soundId);
    if (isDev) console.log(`[AudioPreloader] Transferred: ${soundId}`);
    return entry.element;
  }

  /**
   * Check if a sound is preloaded and ready.
   */
  isReady(soundId: string): boolean {
    const entry = this.preloadedAudio.get(soundId);
    return entry?.ready ?? false;
  }

  /**
   * Cancel preloading for a sound (e.g., when card scrolls out of view).
   */
  cancel(soundId: string): void {
    const entry = this.preloadedAudio.get(soundId);
    if (entry) {
      entry.element.src = '';
      entry.element.load();
      this.preloadedAudio.delete(soundId);
    }
    this.loadingPromises.delete(soundId);
  }

  /**
   * Clear all preloaded audio (useful for cleanup).
   */
  clear(): void {
    for (const [, entry] of this.preloadedAudio) {
      entry.element.src = '';
      entry.element.load();
    }
    this.preloadedAudio.clear();
    this.loadingPromises.clear();
  }

  private evictIfNeeded(): void {
    if (this.preloadedAudio.size < this.maxPreloaded) {
      return;
    }

    // Find oldest entry
    let oldestId: string | null = null;
    let oldestTimestamp = Infinity;

    for (const [id, entry] of this.preloadedAudio) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestId = id;
      }
    }

    if (oldestId) {
      const entry = this.preloadedAudio.get(oldestId);
      if (entry) {
        entry.element.src = '';
        entry.element.load();
      }
      this.preloadedAudio.delete(oldestId);
      if (isDev) console.log(`[AudioPreloader] Evicted: ${oldestId}`);
    }
  }
}

// Export singleton instance
export const audioPreloader = AudioPreloader.getInstance();
